// Paket P1 — PromoBanner.
//
// Kompakte Inline-Karte mit Promo-Verlaufs-Rand (border-image),
// Icon links, Titel + eine Zeile Text, CTA rechts, X oben rechts.
// role="region", aria-label = Titel.
//
// Auf schmalen Viewports (unter 600 px) stapelt die Karte Icon/Copy und
// CTA untereinander — der bestehende Breakpoint 600 ist im Set, also
// zaehlt er nicht gegen den Ratchet.
//
// ⚠️ Wird AUCH vom Nicht-Promo-Hinweis „Bestandsschutz endet" (Paket A12,
// app/BestandsschutzEndHost.tsx) genutzt, der bewusst NICHT zum Promo-System
// gehoert. Wer die Mitgliedschafts-Promos abschaltet oder hier einen globalen
// Schalter „Banner aus" einbaut, muss diesen Hinweis davon ausnehmen (er ist eine
// Information ueber das Ende einer zugesagten Leistung, keine Werbung) und das
// Banner-Bauteil selbst stehen lassen.
//
// Paket A12 (additiv, Standardverhalten unveraendert): `fullText` hebt die
// Zwei-Zeilen-Kappung auf (Hinweise mit vollstaendigem Satz), `ctaIcon={null}`
// nimmt den Chevron vom CTA, `secondaryLabel`/`onSecondary` ergaenzen einen
// zweiten, leisen Knopf neben dem CTA.

import { useId } from 'react';
import { Icon, type IconName } from '../ui';
import { useT } from '../../i18n';
import type { TierMarkTier } from './TierMark';
import styles from './PromoBanner.module.css';

export interface PromoBannerProps {
  /** Eindeutige ID fuer dismiss-Speicherung in sessionStorage (P2). */
  id: string;
  title: string;
  text: string;
  ctaLabel: string;
  onCta: () => void;
  onDismiss?: () => void;
  /** Zeigt den Text vollstaendig statt auf zwei Zeilen gekappt (Default: gekappt). */
  fullText?: boolean;
  /** Icon im CTA; `null` = ohne Icon, ohne Angabe der Chevron. */
  ctaIcon?: IconName | null;
  /** Leiser zweiter Knopf neben dem CTA (nur gerendert, wenn beide gesetzt sind). */
  secondaryLabel?: string;
  onSecondary?: () => void;
  tier?: TierMarkTier;
  icon?: IconName;
  /** Klassen-Hook (z. B. fuer margin-block in der einbettenden Seite). */
  className?: string;
}

const TIER_RGB: Record<TierMarkTier, string> = {
  plus: 'var(--ff-tier-plus-rgb)',
  ultra: 'var(--ff-tier-ultra-rgb)',
  founders: 'var(--ff-tier-founders-rgb)',
  torchbearer: 'var(--ff-tier-founders-rgb)',
};

export function PromoBanner({
  id,
  title,
  text,
  ctaLabel,
  onCta,
  onDismiss,
  fullText = false,
  ctaIcon,
  secondaryLabel,
  onSecondary,
  tier = 'ultra',
  icon = 'flame',
  className,
}: PromoBannerProps) {
  const { t } = useT();
  const titleId = useId();
  // CSS-Variable pro Instanz, damit border-image den Tier-Ton trifft.
  const cssVars: Record<string, string> = { '--tier-rgb': TIER_RGB[tier] };
  const cta = (
    <button
      type="button"
      className={styles.cta}
      data-testid={`promo-banner-cta-${id}`}
      onClick={onCta}
    >
      {ctaLabel}
      {ctaIcon === null ? null : <Icon name={ctaIcon ?? 'chevron-right'} size={16} directional />}
    </button>
  );
  return (
    <section
      role="region"
      aria-labelledby={titleId}
      data-promo-banner={id}
      data-tier={tier}
      className={[styles.banner, className].filter(Boolean).join(' ')}
      style={cssVars as never}
    >
      <span className={styles.iconWrap} aria-hidden="true">
        <Icon name={icon} size={24} />
      </span>
      <div className={styles.copy}>
        <h3 id={titleId} className={styles.title}>{title}</h3>
        <p className={fullText ? `${styles.text} ${styles.textFull}` : styles.text}>{text}</p>
        {secondaryLabel && onSecondary ? (
          <div className={styles.actions}>
            {cta}
            <button
              type="button"
              className={styles.secondary}
              data-testid={`promo-banner-secondary-${id}`}
              onClick={onSecondary}
            >
              {secondaryLabel}
            </button>
          </div>
        ) : cta}
      </div>
      {onDismiss ? (
        <button
          type="button"
          className={styles.dismiss}
          aria-label={t('promo.dismiss')}
          data-testid={`promo-banner-dismiss-${id}`}
          onClick={onDismiss}
        >
          <Icon name="close" size={18} />
        </button>
      ) : null}
    </section>
  );
}

export default PromoBanner;
